import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, Button, Title, Label } from '@patternfly/react-core'; // Adjust imports
import LambdaInsights from './LambdaInsights'; // Adjust as needed
import { Widget } from '../../types/dashboards/dashboard';
import { LambdaInsightsResponse } from '../../api/types';
import { faArrowLeft, faSparkles, faTimes } from '@fortawesome/pro-regular-svg-icons';
import Loader from '../util/Loader';
import { TDashboardInightsEntity } from '../../api/shared-entity/SharedEntity';

interface InsightsPanelProps {
	widgets: Widget[];
	lambdaInsights?: { entityId: number; insightsResponse: LambdaInsightsResponse }[];
	setLambdaInsights: React.Dispatch<
		React.SetStateAction<{ entityId: number; insightsResponse: LambdaInsightsResponse }[]>
	>;
	isInsightsEvalutor: boolean;
	isInsightsOpen: boolean;
	chosenInsightWidgets: Widget[];
	setChosenInsightWidgets: React.Dispatch<React.SetStateAction<Widget[]>>;
	setIsInsightsOpen: (open: boolean) => void;
	setIsInsightsEvalutor: (isEval: boolean) => void;
	beginInsightsRequest: () => void;
	openDashboardInsightsPanel: (open: boolean) => void;
	selectedChartWidget: Widget | undefined;
	setSelectedChartWidget: React.Dispatch<React.SetStateAction<Widget | undefined>>;
	insightsEntities: TDashboardInightsEntity[];
}

const InsightsDashboardPanel: React.FC<InsightsPanelProps> = ({
	widgets,
	lambdaInsights,
	setLambdaInsights,
	isInsightsEvalutor,
	isInsightsOpen,
	chosenInsightWidgets,
	setChosenInsightWidgets,
	setIsInsightsOpen,
	setIsInsightsEvalutor,
	beginInsightsRequest,
	openDashboardInsightsPanel,
	selectedChartWidget,
	setSelectedChartWidget,
	insightsEntities,
}) => {
	const [insightsData, setInsightsData] = useState<LambdaInsightsResponse>({
		insights: [],
		actions: [],
	});
	const [loadedAtLeastOneInsight, setLoadedAtLeastOneInsight] = useState<boolean>(false);
	const [loadedWidgetIds, setLoadedWidgetIds] = useState<number[]>([]);

	const updateInsightWidgets = (checked: boolean, widgetId: number) => {
		const widget = widgets.find((x) => x.id === widgetId);
		const exists = chosenInsightWidgets.some((x) => x.id === widgetId);

		if (checked && widget && !exists) {
			const nameFromWidget = getNameFromWidget(widget) ?? '';
			setChosenInsightWidgets((prev) => [...prev, { ...widget, name: nameFromWidget }]);
		} else if (widget) {
			setChosenInsightWidgets((prev) => prev.filter((x) => x.id !== widgetId));
		}
	};

	useEffect(() => {
		if (!lambdaInsights) return;

		// Step 1: Track all widgets that are fully loaded
		const newlyLoadedIds: number[] = [];

		lambdaInsights.forEach(({ entityId, insightsResponse }) => {
			if (insightsResponse?.actions?.length > 0 || insightsResponse?.insights?.length > 0) {
				// We assume `entityId` maps directly to widget.id
				newlyLoadedIds.push(entityId);
			}
		});

		// Step 2: Update list of loaded widgets
		if (newlyLoadedIds.length > 0) {
			setLoadedAtLeastOneInsight(true); // used for global spinner
			setLoadedWidgetIds((prev) => [...new Set([...prev, ...newlyLoadedIds])]);
		}

		// Step 3: Update insights for selected chart widget (only if data is present)
		if (selectedChartWidget) {
			let found = lambdaInsights.find((x) => x.entityId === selectedChartWidget.chart);

			//if chart not found check for report next
			if (!found) {
				found = lambdaInsights.find((x) => x.entityId === selectedChartWidget.report);
			}

			if (
				(found && found?.insightsResponse?.actions?.length > 0) ||
				(found && found?.insightsResponse?.insights?.length > 0)
			) {
				setInsightsData(found.insightsResponse);
			}
		}
	}, [lambdaInsights, selectedChartWidget]);

	const getNameFromWidget = (widget: Widget) => {
		if (widget.widget_type == 'chart') {
			const chartName =
				insightsEntities.find((x) => x.chart?.id == widget.chart)?.chart?.name ?? '';
			return chartName;
		}

		if (widget.widget_type == 'pivot_table') {
			const tableNameName =
				insightsEntities.find(
					(x) => x.report?.id == widget.report || x.table?.id == widget.report
				)?.report?.name ?? '';
			return tableNameName;
		}
	};

	const insightWidgets = widgets.filter(
		(x) => x.widget_type === 'chart' || x.widget_type == 'pivot_table'
	);

	return (
		<div>
			<div className="dv-insights">
				<div className="pull-right close-insights">
					<FontAwesomeIcon
						icon={faTimes}
						size="2x"
						onClick={() => {
							setIsInsightsOpen(false);
							openDashboardInsightsPanel(false);
							setIsInsightsEvalutor(false);
							setSelectedChartWidget(undefined);
							setLambdaInsights([]);
							setLoadedWidgetIds([]);
							setInsightsData({ actions: [], insights: [] });
							setLoadedAtLeastOneInsight(false);
						}}
					/>
				</div>
				<div className="insights-header">
					<FontAwesomeIcon
						icon={faSparkles}
						size="2x"
						color="#0073c3"
					/>
					<Title
						headingLevel="h1"
						data-testid="header-insights"
					>
						AI * Insights
					</Title>
				</div>
			</div>
			<br />

			{!isInsightsEvalutor && (
				<>
					<div>
						{insightWidgets.map((widget) => (
							<div
								className="checkbox-row"
								key={widget.id}
							>
								<Checkbox
									id={`widget_chart_${widget.id ?? '0'}`}
									label={getNameFromWidget(widget)}
									className="large-checkbox"
									onChange={(checked) =>
										updateInsightWidgets(checked, widget.id ?? 0)
									}
									isChecked={
										chosenInsightWidgets.some((x) => x.id == widget.id)
											? true
											: false
									}
								/>
							</div>
						))}
					</div>
					<div className="dv-insights">
						<Button
							key="generate_graph_summary"
							variant="primary"
							className="btn-graph-summary"
							onClick={() => {
								beginInsightsRequest();
							}}
							isDisabled={chosenInsightWidgets.length == 0}
						>
							AI * Insights
						</Button>
					</div>
					<hr />
					<br />
				</>
			)}

			{isInsightsEvalutor && (
				<div className="sidebar-menu">
					<div
						className="back-button"
						onClick={() => {
							setIsInsightsEvalutor(false);
							setChosenInsightWidgets([]);
							setSelectedChartWidget(undefined);
							setLambdaInsights([]);
							setLoadedWidgetIds([]);
							setInsightsData({ actions: [], insights: [] });
							setLoadedAtLeastOneInsight(false);
						}}
					>
						<FontAwesomeIcon
							icon={faArrowLeft}
							className="back-icon"
							onClick={() => {
								setIsInsightsEvalutor(false);
								setChosenInsightWidgets([]);
								setSelectedChartWidget(undefined);
								setLambdaInsights([]);
								setLoadedWidgetIds([]);
								setInsightsData({ actions: [], insights: [] });
								setLoadedAtLeastOneInsight(false);
							}}
						/>
						<span>Back</span>
					</div>
					<hr />
					<div>
						{/* <label>Overview</label> */}
						<span className="spacer-xs"></span>
						{chosenInsightWidgets.map((widget, idx) => (
							<>
								<React.Fragment key={widget.id}>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<Label
											style={{
												width: '3500px',
												cursor: 'pointer',
												padding: '10px',
											}}
											className={
												widget.id === selectedChartWidget?.id
													? 'selected-chart-widget'
													: ''
											}
											onClick={() => {
												setSelectedChartWidget(widget);
											}}
										>
											{widget.name}
										</Label>

										{/* {!loadedWidgetIds.includes(widget.id ?? 0) && ( */}
										{!loadedAtLeastOneInsight && (
											<Loader
												customClass="custom-loader-right"
												size="md"
											/>
										)}
									</div>
								</React.Fragment>
								<span className="spacer-xxs"></span>
							</>
						))}
						<br />
					</div>
					<br />
					<hr />
					<br />
					{!loadedAtLeastOneInsight && (
						<div className="text-center">
							<Loader customClass="custom-spinner-scale" /> <br />
							<label>Please wait, generating insights…</label>
						</div>
					)}

					{(insightsData?.actions?.length > 0 || insightsData?.insights?.length > 0) && (
						<>
							<LambdaInsights
								insights={insightsData}
								hasBackground={false}
							/>
							<hr />
							<br />
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default InsightsDashboardPanel;
